import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Search, Filter, MapPin, Clock, Briefcase, DollarSign, Star, BookmarkIcon as BookmarkOutline, BookmarkIcon, ChevronDown, AlertTriangle, X, Users, Building2, Languages, Award, Heart, Bookmark, Map, Crown, Calendar, Upload, FileText, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';

// Import UI components
import UnifiedButton from '../../../components/unified/UnifiedButton';
import UnifiedInput from '../../../components/unified/UnifiedInput';
import UnifiedCard from '../../../components/unified/UnifiedCard';
import Select from '../../../components/ui/Select';
import MultiSelect from '../../../components/ui/MultiSelect';
import Chip from '../../../components/ui/Chip';

// Import design tokens
import { typography, colors } from '../../../styles/design-tokens';

// Import search fallback
import { deterministicSearchEngine, searchJobs, initializeSearch } from '../../../lib/search_fallback';

import { searchData } from '../../../mocks/search';
import Input from '../../../components/ui/Input';

// Import application service
import { submitApplication, checkDuplicateApplication } from '../../../services/jobApplicationService';

// Import auth context
import { useAuth } from '../../../contexts/AuthContext';

// Import saved jobs service
import { saveJob, unsaveJob } from '../../../services/savedJobsService';

import { semanticJobSearch } from '../../../services/semanticSearchService';
import { addToSearchHistory } from '../../../services/searchHistoryService';
import { saveFilterState, loadFilterState } from '../../../services/filterPersistenceService';

const Jobs = () => {
  const navigate = useNavigate();
  const authContext = useAuth();
  const { user = null } = authContext || {};
  const [searchQuery, setSearchQuery] = useState('');
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedJobs, setSavedJobs] = useState(new Set()); // Empty initialization - load from Supabase
  const [savedJobsLoading, setSavedJobsLoading] = useState(false);
  
  // Application modal state
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    resumeFile: null,
    coverLetter: ''
  });
  const [applicationStatus, setApplicationStatus] = useState({
    submitting: false,
    error: null,
    success: false
  });
  const [duplicateCheck, setDuplicateCheck] = useState({
    checking: false,
    isDuplicate: false
  });
  
  // Enhanced filter state (consistent with public pages)
  const [filters, setFilters] = useState({
    query: '',
    location: { city: '' },
    workMode: 'ANY',
    datePosted: 'ANY',
    jobTypes: [],
    experienceLevel: [],
    industries: [],
    companySize: [],
    skills: [],
    languages: [],
    benefits: [],
    salaryRange: { min: '', max: '', currency: 'USD' }
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showMapView, setShowMapView] = useState(false);
  const [savedSearchName, setSavedSearchName] = useState('');
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  
  // Search capabilities state (no fallback button toggle)
  const [searchCapabilities, setSearchCapabilities] = useState(null);
  const [fallbackInitialized, setFallbackInitialized] = useState(false);
  const [useSemanticSearch, setUseSemanticSearch] = useState(true);
  const [semanticResults, setSemanticResults] = useState(null);

  // Load saved jobs from Supabase on component mount
  useEffect(() => {
    if (user?.id) {
      loadSavedJobsFromSupabase();
    }
  }, [user?.id]);

  // Initialize search fallback on component mount
  useEffect(() => {
    const initializeFallback = async () => {
      try {
        const jobData = searchData?.filter(item => !item?.type || item?.type === 'job') || [];
        const initResult = await initializeSearch(jobData);
        
        if (initResult?.success) {
          setFallbackInitialized(true);
          console.log('Dashboard search fallback initialized');
        }
      } catch (error) {
        console.error('Failed to initialize dashboard search:', error);
      }
    };

    initializeFallback();
  }, []);

  // Load persisted filters on mount
  useEffect(() => {
    const savedFilters = loadFilterState();
    if (savedFilters) {
      setFilters(savedFilters);
    }
  }, []);

  // Save filters when they change
  useEffect(() => {
    if (hasActiveFilters()) {
      saveFilterState(filters);
    }
  }, [filters]);

  // Initial job load
  useEffect(() => {
    loadJobs();
  }, []);

  // Apply search and filters
  useEffect(() => {
    if (jobs?.length > 0) {
      performSearch();
    }
  }, [searchQuery, filters, sortBy, jobs]);

  // Enhanced location options (consistent with public pages)
  const locationOptions = [
    { value: '', label: 'All Locations', group: 'Global' },
    { value: 'Remote', label: 'Remote', group: 'Work Mode' },
    { value: 'San Francisco', label: 'San Francisco, CA', group: 'North America' },
    { value: 'New York', label: 'New York, NY', group: 'North America' },
    { value: 'Toronto', label: 'Toronto, ON', group: 'North America' },
    { value: 'Vancouver', label: 'Vancouver, BC', group: 'North America' },
    { value: 'London', label: 'London, UK', group: 'Europe' },
    { value: 'Berlin', label: 'Berlin, Germany', group: 'Europe' },
    { value: 'Amsterdam', label: 'Amsterdam, Netherlands', group: 'Europe' },
    { value: 'Paris', label: 'Paris, France', group: 'Europe' },
    { value: 'Sydney', label: 'Sydney, Australia', group: 'Asia-Pacific' },
    { value: 'Singapore', label: 'Singapore', group: 'Asia-Pacific' },
    { value: 'Tokyo', label: 'Tokyo, Japan', group: 'Asia-Pacific' }
  ];

  const jobTypeOptions = [
    { label: 'Full-time', value: 'full-time', icon: 'Briefcase' },
    { label: 'Part-time', value: 'part-time', icon: 'Clock' },
    { label: 'Contract', value: 'contract', icon: 'FileText' },
    { label: 'Freelance', value: 'freelance', icon: 'Users' },
    { label: 'Internship', value: 'internship', icon: 'GraduationCap' },
    { label: 'Volunteer', value: 'volunteer', icon: 'Heart' }
  ];

  const experienceLevelOptions = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (6+ years)' },
    { value: 'lead', label: 'Lead/Principal' },
    { value: 'executive', label: 'Executive/C-Level' }
  ];

  const industryOptions = [
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance & Banking' },
    { value: 'education', label: 'Education' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'marketing', label: 'Marketing & Advertising' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'non-profit', label: 'Non-profit' },
    { value: 'government', label: 'Government' },
    { value: 'media', label: 'Media & Entertainment' },
    { value: 'real-estate', label: 'Real Estate' }
  ];

  const companySizeOptions = [
    { value: 'startup', label: 'Startup (1-50)', icon: 'Zap' },
    { value: 'small', label: 'Small (51-200)', icon: 'Building' },
    { value: 'medium', label: 'Medium (201-1000)', icon: 'Building2' },
    { value: 'large', label: 'Large (1001+)', icon: 'Buildings' }
  ];

  const skillsOptions = [
    // Technical Skills
    { value: 'javascript', label: 'JavaScript', category: 'Technical' },
    { value: 'react', label: 'React', category: 'Technical' },
    { value: 'python', label: 'Python', category: 'Technical' },
    { value: 'nodejs', label: 'Node.js', category: 'Technical' },
    { value: 'typescript', label: 'TypeScript', category: 'Technical' },
    { value: 'aws', label: 'AWS', category: 'Technical' },
    { value: 'docker', label: 'Docker', category: 'Technical' },
    { value: 'kubernetes', label: 'Kubernetes', category: 'Technical' },
    { value: 'sql', label: 'SQL', category: 'Technical' },
    { value: 'git', label: 'Git', category: 'Technical' },
    // Soft Skills
    { value: 'leadership', label: 'Leadership', category: 'Soft Skills' },
    { value: 'communication', label: 'Communication', category: 'Soft Skills' },
    { value: 'project-management', label: 'Project Management', category: 'Soft Skills' },
    { value: 'problem-solving', label: 'Problem Solving', category: 'Soft Skills' },
    { value: 'teamwork', label: 'Teamwork', category: 'Soft Skills' }
  ];

  const languageOptions = [
    { value: 'english', label: 'English' },
    { value: 'spanish', label: 'Spanish' },
    { value: 'french', label: 'French' },
    { value: 'german', label: 'German' },
    { value: 'mandarin', label: 'Mandarin Chinese' },
    { value: 'japanese', label: 'Japanese' },
    { value: 'portuguese', label: 'Portuguese' },
    { value: 'italian', label: 'Italian' },
    { value: 'russian', label: 'Russian' },
    { value: 'arabic', label: 'Arabic' }
  ];

  const benefitsOptions = [
    { value: 'health-insurance', label: 'Health Insurance', icon: 'Heart' },
    { value: 'dental-insurance', label: 'Dental Insurance', icon: 'Smile' },
    { value: 'vision-insurance', label: 'Vision Insurance', icon: 'Eye' },
    { value: 'retirement-plan', label: '401(k) / Retirement Plan', icon: 'PiggyBank' },
    { value: 'paid-time-off', label: 'Paid Time Off', icon: 'Calendar' },
    { value: 'flexible-schedule', label: 'Flexible Schedule', icon: 'Clock' },
    { value: 'remote-work', label: 'Remote Work Options', icon: 'Home' },
    { value: 'professional-development', label: 'Professional Development', icon: 'BookOpen' },
    { value: 'gym-membership', label: 'Gym Membership', icon: 'Dumbbell' },
    { value: 'stock-options', label: 'Stock Options/Equity', icon: 'TrendingUp' }
  ];

  // Premium filter options (available for authenticated job seekers)
  const premiumFilters = [
    {
      key: 'culturalFit',
      label: 'Cultural Fit',
      icon: Heart,
      description: 'Match jobs based on company culture and values',
      options: [
        { value: 'innovative', label: 'Innovation-focused' },
        { value: 'collaborative', label: 'Collaborative' },
        { value: 'autonomous', label: 'Autonomous work' },
        { value: 'growth-oriented', label: 'Growth-oriented' },
        { value: 'work-life-balance', label: 'Work-life balance' }
      ]
    },
    {
      key: 'aiMatchScore',
      label: 'AI Match Score',
      icon: Star,
      description: 'Filter by minimum AI compatibility score',
      type: 'range',
      min: 60,
      max: 100
    },
    {
      key: 'skillGapAnalysis',
      label: 'Skill Development',
      icon: Award,
      description: 'Find jobs that help develop specific skills',
      options: [
        { value: 'leadership', label: 'Leadership development' },
        { value: 'technical', label: 'Technical advancement' },
        { value: 'management', label: 'Management skills' },
        { value: 'specialization', label: 'Skill specialization' }
      ]
    }
  ];

  /**
   * Load user's saved jobs from Supabase
   */
  const loadSavedJobsFromSupabase = async () => {
    if (!user?.id) return;

    try {
      const { getSavedJobs } = await import('../../../services/savedJobsService');
      const result = await getSavedJobs();

      if (result?.success && result?.data) {
        const savedJobIds = new Set(result.data.map(item => item.jobId));
        setSavedJobs(savedJobIds);
      }
    } catch (error) {
      console.error('Failed to load saved jobs:', error);
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      // Simulate API call - in real app this would fetch user's recommended jobs
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Get sample jobs from mock data
      const jobData = searchData?.filter(item => !item?.type || item?.type === 'job') || [];
      const shuffledJobs = jobData?.sort(() => 0.5 - Math.random())?.slice(0, 20);
      
      setJobs(shuffledJobs);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      setJobs([]);
    }
    setLoading(false);
  };

  // Enhanced performSearch with semantic search option
  const performSearch = async () => {
    if (!jobs?.length) return;

    try {
      // Add to search history if there's a query
      if (searchQuery?.trim()) {
        addToSearchHistory(searchQuery, filters, filteredJobs?.length);
      }

      // Check if semantic search should be used
      if (useSemanticSearch && searchQuery?.trim()) {
        setSearchCapabilities({ mode: 'AI-powered', reason: 'Using Gemini semantic search' });
        
        try {
          const semanticResult = await semanticJobSearch(searchQuery, jobs, {
            limit: 20,
            includeReasons: true
          });
          
          setSemanticResults(semanticResult);
          setFilteredJobs(semanticResult?.results);
          return;
        } catch (semanticError) {
          // Fall back to regular search
          console.log('Semantic search failed, using fallback:', semanticError?.message);
          setUseSemanticSearch(false);
        }
      }

      // Regular search and filter logic (existing code)
      const aiAvailable = Math.random() > 0.4;
      
      if (aiAvailable && searchQuery?.trim()) {
        setSearchCapabilities(null);
        let filtered = [...jobs];
        
        if (searchQuery?.trim()) {
          const queryLower = searchQuery?.toLowerCase();
          filtered = filtered?.filter(job =>
            job?.title?.toLowerCase()?.includes(queryLower) ||
            job?.companyName?.toLowerCase()?.includes(queryLower) ||
            job?.tags?.some(tag => tag?.toLowerCase()?.includes(queryLower))
          );
        }
        
        filtered = applyAdvancedFilters(filtered);
        setFilteredJobs(filtered);
      } else {
        if (fallbackInitialized && deterministicSearchEngine?.isReady()) {
          const searchResponse = await searchJobs(searchQuery, {
            filters: {
              location: filters?.location?.city ? { city: filters?.location?.city } : null,
              workMode: filters?.workMode !== 'ANY' ? filters?.workMode : null,
              experienceLevel: filters?.experienceLevel?.length > 0 ? filters?.experienceLevel : null,
              datePosted: filters?.datePosted !== 'ANY' ? filters?.datePosted : null
            },
            limit: 20,
            userLocation: null
          });

          setFilteredJobs(searchResponse?.results || []);
          setSearchCapabilities(searchResponse?.fallback);
        } else {
          setSearchCapabilities({
            mode: 'basic',
            reason: 'Search engine initializing',
            capabilities: ['basic filtering'],
            limitations: ['limited relevance']
          });

          let filtered = applyAdvancedFilters([...jobs]);
          
          if (searchQuery?.trim()) {
            const queryLower = searchQuery?.toLowerCase();
            filtered = filtered?.filter(job =>
              job?.title?.toLowerCase()?.includes(queryLower) ||
              job?.companyName?.toLowerCase()?.includes(queryLower)
            );
          }
          
          setFilteredJobs(filtered);
        }
      }
    } catch (error) {
      console.error('Search failed:', error);
      setFilteredJobs(applyAdvancedFilters([...jobs]));
      setSearchCapabilities({
        mode: 'error',
        reason: 'Search error',
        limitations: ['basic results only']
      });
    }
  };

  const applyAdvancedFilters = (jobList) => {
    let filtered = [...jobList];

    // Location filter
    if (filters?.location?.city && filters?.location?.city !== '') {
      filtered = filtered?.filter(job =>
        job?.locations?.some(loc =>
          typeof loc === 'string' 
            ? loc?.toLowerCase()?.includes(filters?.location?.city?.toLowerCase())
            : loc?.city?.toLowerCase()?.includes(filters?.location?.city?.toLowerCase())
        )
      );
    }

    // Work mode filter
    if (filters?.workMode && filters?.workMode !== 'ANY') {
      filtered = filtered?.filter(job => job?.workMode === filters?.workMode);
    }

    // Date posted filter
    if (filters?.datePosted && filters?.datePosted !== 'ANY') {
      const now = new Date();
      const cutoffDates = {
        'Today': new Date(now - 24 * 60 * 60 * 1000),
        'This Week': new Date(now - 7 * 24 * 60 * 60 * 1000),
        'This Month': new Date(now - 30 * 24 * 60 * 60 * 1000),
        'Last 3 Months': new Date(now - 90 * 24 * 60 * 60 * 1000)
      };
      const cutoff = cutoffDates?.[filters?.datePosted];
      if (cutoff) {
        filtered = filtered?.filter(job => new Date(job?.postedAt) >= cutoff);
      }
    }

    // Job types filter
    if (filters?.jobTypes && filters?.jobTypes?.length > 0) {
      filtered = filtered?.filter(job => 
        filters?.jobTypes?.some(type => 
          job?.employmentType?.toLowerCase()?.includes(type?.toLowerCase())
        )
      );
    }

    // Experience level filter
    if (filters?.experienceLevel && filters?.experienceLevel?.length > 0) {
      filtered = filtered?.filter(job => 
        filters?.experienceLevel?.includes(job?.experienceLevel?.toLowerCase())
      );
    }

    // Industries filter
    if (filters?.industries && filters?.industries?.length > 0) {
      filtered = filtered?.filter(job => 
        filters?.industries?.some(industry => 
          job?.industry?.toLowerCase()?.includes(industry?.toLowerCase())
        )
      );
    }

    // Company size filter
    if (filters?.companySize && filters?.companySize?.length > 0) {
      filtered = filtered?.filter(job => 
        filters?.companySize?.includes(job?.companySize?.toLowerCase())
      );
    }

    // Skills filter
    if (filters?.skills && filters?.skills?.length > 0) {
      filtered = filtered?.filter(job => 
        filters?.skills?.some(skill => 
          job?.tags?.some(tag => tag?.toLowerCase()?.includes(skill?.toLowerCase()))
        )
      );
    }

    // Languages filter
    if (filters?.languages && filters?.languages?.length > 0) {
      filtered = filtered?.filter(job => 
        filters?.languages?.some(lang => 
          job?.languages?.some(jobLang => jobLang?.toLowerCase()?.includes(lang?.toLowerCase()))
        )
      );
    }

    // Salary range filter
    if (filters?.salaryRange?.min || filters?.salaryRange?.max) {
      filtered = filtered?.filter(job => {
        const minSalary = job?.salary?.min || 0;
        const maxSalary = job?.salary?.max || 999999999;
        const filterMin = parseInt(filters?.salaryRange?.min) || 0;
        const filterMax = parseInt(filters?.salaryRange?.max) || 999999999;
        
        return maxSalary >= filterMin && minSalary <= filterMax;
      });
    }

    // Apply sorting
    if (sortBy === 'date') {
      filtered?.sort((a, b) => new Date(b?.postedAt) - new Date(a?.postedAt));
    } else if (sortBy === 'salary') {
      filtered?.sort((a, b) => (b?.salary?.max || 0) - (a?.salary?.max || 0));
    } else if (sortBy === 'company') {
      filtered?.sort((a, b) => a?.companyName?.localeCompare(b?.companyName));
    }

    return filtered;
  };

  const updateFilter = (key, newValue) => {
    setFilters(prev => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const handleSearch = () => {
    performSearch();
  };

  const clearAllFilters = () => {
    setFilters({
      query: '',
      location: { city: '' },
      workMode: 'ANY',
      datePosted: 'ANY',
      jobTypes: [],
      experienceLevel: [],
      industries: [],
      companySize: [],
      skills: [],
      languages: [],
      benefits: [],
      salaryRange: { min: '', max: '', currency: 'USD' }
    });
    setSearchQuery('');
  };

  /**
   * Toggle save/unsave job with Supabase integration
   */
  const toggleSaveJob = async (jobId) => {
    if (!user?.id) {
      // User not authenticated - show message or redirect to login
      alert('Please log in to save jobs');
      return;
    }

    setSavedJobsLoading(true);

    try {
      const isSaved = savedJobs?.has(jobId);

      if (isSaved) {
        // Unsave the job
        const result = await unsaveJob(jobId);

        if (result?.success) {
          setSavedJobs(prev => {
            const newSet = new Set(prev);
            newSet?.delete(jobId);
            return newSet;
          });
        } else {
          throw new Error(result?.error?.message || 'Failed to unsave job');
        }
      } else {
        // Save the job
        const result = await saveJob(jobId);

        if (result?.success) {
          setSavedJobs(prev => new Set(prev)?.add(jobId));
        } else if (result?.error?.code === 'DUPLICATE') {
          // Job already saved - update local state
          setSavedJobs(prev => new Set(prev)?.add(jobId));
        } else {
          throw new Error(result?.error?.message || 'Failed to save job');
        }
      }
    } catch (error) {
      console.error('Error toggling saved job:', error);
      alert(error?.message || 'Failed to update saved job status');
    } finally {
      setSavedJobsLoading(false);
    }
  };

  const hasActiveFilters = () => {
    return (
      (filters?.location?.city && filters?.location?.city !== '') ||
      (filters?.workMode && filters?.workMode !== 'ANY') ||
      (filters?.datePosted && filters?.datePosted !== 'ANY') ||
      (filters?.jobTypes && filters?.jobTypes?.length > 0) ||
      (filters?.experienceLevel && filters?.experienceLevel?.length > 0) ||
      (filters?.industries && filters?.industries?.length > 0) ||
      (filters?.companySize && filters?.companySize?.length > 0) ||
      (filters?.skills && filters?.skills?.length > 0) ||
      (filters?.languages && filters?.languages?.length > 0) ||
      (filters?.benefits && filters?.benefits?.length > 0) ||
      (filters?.salaryRange?.min || filters?.salaryRange?.max)
    );
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.location?.city && filters?.location?.city !== '') count++;
    if (filters?.workMode && filters?.workMode !== 'ANY') count++;
    if (filters?.datePosted && filters?.datePosted !== 'ANY') count++;
    if (filters?.jobTypes && filters?.jobTypes?.length > 0) count += filters?.jobTypes?.length;
    if (filters?.experienceLevel && filters?.experienceLevel?.length > 0) count += filters?.experienceLevel?.length;
    if (filters?.industries && filters?.industries?.length > 0) count += filters?.industries?.length;
    if (filters?.companySize && filters?.companySize?.length > 0) count += filters?.companySize?.length;
    if (filters?.skills && filters?.skills?.length > 0) count += filters?.skills?.length;
    if (filters?.languages && filters?.languages?.length > 0) count += filters?.languages?.length;
    if (filters?.benefits && filters?.benefits?.length > 0) count += filters?.benefits?.length;
    if (filters?.salaryRange?.min || filters?.salaryRange?.max) count++;
    return count;
  };

  const handleSaveSearch = () => {
    if (!savedSearchName?.trim()) return;
    
    const searchData = {
      name: savedSearchName?.trim(),
      filters: { ...filters },
      createdAt: new Date()?.toISOString(),
      resultCount: filteredJobs?.length || 0
    };
    
    const savedSearches = JSON.parse(localStorage?.getItem('savedSearches') || '[]');
    savedSearches?.push(searchData);
    localStorage?.setItem('savedSearches', JSON.stringify(savedSearches));
    
    setSavedSearchName('');
    setShowSaveSearchModal(false);
    
    alert(`Search "${searchData?.name}" saved successfully!`);
  };

  const formatSalary = (salary) => {
    if (!salary) return null;
    if (salary?.min && salary?.max) {
      return `$${(salary?.min / 1000)?.toFixed(0)}k - $${(salary?.max / 1000)?.toFixed(0)}k`;
    }
    return null;
  };

  const formatLocation = (locations) => {
    if (!locations?.length) return 'Remote';
    const firstLoc = locations?.[0];
    return typeof firstLoc === 'string' ? firstLoc : `${firstLoc?.city}, ${firstLoc?.region}`;
  };

  const getWorkModeIcon = (workMode) => {
    switch (workMode?.toLowerCase()) {
      case 'remote': return '🏠';
      case 'hybrid': return '🏢';
      case 'onsite': return '🏢';
      default: return '💼';
    }
  };

  /**
   * Opens application modal for a specific job
   */
  const handleApplyClick = async (job) => {
    setSelectedJob(job);
    setShowApplicationModal(true);
    setApplicationForm({ resumeFile: null, coverLetter: '' });
    setApplicationStatus({ submitting: false, error: null, success: false });
    
    // Check if user has already applied
    if (user?.id) {
      setDuplicateCheck({ checking: true, isDuplicate: false });
      try {
        const isDuplicate = await checkDuplicateApplication(job?.id, user?.id);
        setDuplicateCheck({ checking: false, isDuplicate });
      } catch (error) {
        console.error('Error checking duplicate:', error);
        setDuplicateCheck({ checking: false, isDuplicate: false });
      }
    }
  };

  /**
   * Closes application modal
   */
  const handleCloseModal = () => {
    if (!applicationStatus?.submitting) {
      setShowApplicationModal(false);
      setSelectedJob(null);
      setApplicationForm({ resumeFile: null, coverLetter: '' });
      setApplicationStatus({ submitting: false, error: null, success: false });
      setDuplicateCheck({ checking: false, isDuplicate: false });
    }
  };

  /**
   * Handles resume file selection
   */
  const handleResumeChange = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      // Validate file size (5MB max)
      if (file?.size > 5 * 1024 * 1024) {
        setApplicationStatus({
          ...applicationStatus,
          error: 'File size must not exceed 5MB'
        });
        return;
      }
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes?.includes(file?.type)) {
        setApplicationStatus({
          ...applicationStatus,
          error: 'Only PDF and Word documents are allowed'
        });
        return;
      }
      
      setApplicationForm({ ...applicationForm, resumeFile: file });
      setApplicationStatus({ ...applicationStatus, error: null });
    }
  };

  /**
   * Handles cover letter input
   */
  const handleCoverLetterChange = (event) => {
    const value = event?.target?.value;
    if (value?.length <= 5000) {
      setApplicationForm({ ...applicationForm, coverLetter: value });
    }
  };

  /**
   * Submits application
   */
  const handleSubmitApplication = async () => {
    if (!user?.id) {
      setApplicationStatus({
        submitting: false,
        error: 'You must be logged in to apply',
        success: false
      });
      return;
    }

    if (!applicationForm?.resumeFile) {
      setApplicationStatus({
        submitting: false,
        error: 'Please upload your resume',
        success: false
      });
      return;
    }

    setApplicationStatus({ submitting: true, error: null, success: false });

    try {
      const result = await submitApplication({
        job_id: selectedJob?.id,
        candidate_id: user?.id,
        resume_file: applicationForm?.resumeFile,
        cover_letter: applicationForm?.coverLetter
      });

      if (result?.success) {
        setApplicationStatus({
          submitting: false,
          error: null,
          success: true
        });
        
        // Close modal after 2 seconds
        setTimeout(() => {
          handleCloseModal();
        }, 2000);
      } else {
        setApplicationStatus({
          submitting: false,
          error: result?.error?.message || 'Failed to submit application',
          success: false
        });
      }
    } catch (error) {
      setApplicationStatus({
        submitting: false,
        error: error?.message || 'An unexpected error occurred',
        success: false
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Search */}
      <UnifiedCard>
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className={`absolute left-3 top-3 h-4 w-4 ${colors?.text?.muted}`} />
                <UnifiedInput
                  placeholder="Search jobs, companies, or skills..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
              <UnifiedButton onClick={handleSearch} disabled={loading}>
                Search
              </UnifiedButton>
            </div>
            
            {/* AI Search Toggle */}
            {searchQuery?.trim() && (
              <div className="flex items-center gap-2 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useSemanticSearch}
                    onChange={(e) => setUseSemanticSearch(e?.target?.checked)}
                    className="w-4 h-4 rounded text-accent focus:ring-accent"
                  />
                  <span className={`${typography?.caption} ${colors?.text?.secondary}`}>
                    Use AI-powered semantic search
                  </span>
                  <Sparkles className="w-3 h-3 text-primary" />
                </label>
              </div>
            )}

            {/* Semantic Search Results Info */}
            {semanticResults?.intent && (
              <div className={`mt-2 p-2 ${colors?.background?.secondary} rounded-medium`}>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className={`${typography?.caption} ${colors?.text?.primary}`}>
                    AI understood: {semanticResults?.intent?.jobTitle || 'general search'}
                    {semanticResults?.intent?.filters?.workMode && ` • ${semanticResults?.intent?.filters?.workMode}`}
                    {semanticResults?.intent?.filters?.location?.city && ` • ${semanticResults?.intent?.filters?.location?.city}`}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Map View Toggle (authenticated job seekers) */}
            <UnifiedButton
              variant="secondary"
              size="small"
              onClick={() => setShowMapView(!showMapView)}
              className={`flex items-center gap-2 ${showMapView ? 'bg-accent text-primary-foreground' : ''}`}
            >
              <Map className="w-4 h-4" />
              Map
            </UnifiedButton>
            
            {/* Save Search (authenticated job seekers) */}
            {hasActiveFilters() && (
              <UnifiedButton
                variant="secondary"
                size="small"
                onClick={() => setShowSaveSearchModal(true)}
                className="flex items-center gap-2"
              >
                <Bookmark className="w-4 h-4" />
                Save
              </UnifiedButton>
            )}

            <UnifiedButton
              variant="secondary"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters() && (
                <Chip size="sm" variant="accent" className="ml-1">
                  {getActiveFilterCount()}
                </Chip>
              )}
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </UnifiedButton>
          </div>
        </div>

        {/* Search fallback indicator (no button toggle) */}
        {searchCapabilities && (
          <div className={`mt-4 p-3 ${colors?.background?.secondary} border ${colors?.border?.default} rounded-medium`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`h-4 w-4 ${colors?.status?.warning}`} />
                <span className={`${typography?.body?.small} ${colors?.text?.primary}`}>
                  Dashboard search: AI unavailable — showing {searchCapabilities?.mode} results
                </span>
              </div>
              <div className={`${typography?.caption} ${colors?.text?.secondary}`}>
                {searchCapabilities?.capabilities?.join(', ')}
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Filters Panel (consistent with public pages) */}
        {showFilters && (
          <div className={`mt-6 pt-6 border-t ${colors?.border?.default}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-accent" />
                <h3 className={`${typography?.h3} ${colors?.text?.primary}`}>Filters</h3>
                {hasActiveFilters() && (
                  <Chip size="sm" variant="accent" className="ml-2">
                    {getActiveFilterCount()} active
                  </Chip>
                )}
              </div>
            </div>

            <div className="space-y-6">
              {/* Basic Filters Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Location Filter */}
                <div>
                  <label className={`flex items-center space-x-2 ${typography?.label} ${colors?.text?.primary} mb-2`}>
                    <MapPin className="w-4 h-4" />
                    <span>Location</span>
                  </label>
                  <select
                    value={filters?.location?.city || ''}
                    onChange={(e) => updateFilter('location', { city: e?.target?.value })}
                    className={`w-full px-3 py-2 ${colors?.background?.input} border ${colors?.border?.default} rounded-medium ${colors?.text?.primary} ${typography?.body?.small} focus:outline-none focus:ring-2 focus:ring-primary`}
                  >
                    {locationOptions?.map((option) => (
                      <option key={option?.value} value={option?.value}>
                        {option?.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Work Mode Filter */}
                <div>
                  <label className={`flex items-center space-x-2 ${typography?.label} ${colors?.text?.primary} mb-2`}>
                    <Briefcase className="w-4 h-4" />
                    <span>Work Mode</span>
                  </label>
                  <select
                    value={filters?.workMode || 'ANY'}
                    onChange={(e) => updateFilter('workMode', e?.target?.value)}
                    className={`w-full px-3 py-2 ${colors?.background?.input} border ${colors?.border?.default} rounded-medium ${colors?.text?.primary} ${typography?.body?.small} focus:outline-none focus:ring-2 focus:ring-primary`}
                  >
                    <option value="ANY">Any Work Mode</option>
                    <option value="Remote">Remote Only</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="On-site">On-site</option>
                  </select>
                </div>

                {/* Date Posted Filter */}
                <div>
                  <label className={`flex items-center space-x-2 ${typography?.label} ${colors?.text?.primary} mb-2`}>
                    <Calendar className="w-4 h-4" />
                    <span>Date Posted</span>
                  </label>
                  <select
                    value={filters?.datePosted || 'ANY'}
                    onChange={(e) => updateFilter('datePosted', e?.target?.value)}
                    className={`w-full px-3 py-2 ${colors?.background?.input} border ${colors?.border?.default} rounded-medium ${colors?.text?.primary} ${typography?.body?.small} focus:outline-none focus:ring-2 focus:ring-primary`}
                  >
                    <option value="ANY">Any Date</option>
                    <option value="Today">Today</option>
                    <option value="This Week">This Week</option>
                    <option value="This Month">This Month</option>
                    <option value="Last 3 Months">Last 3 Months</option>
                  </select>
                </div>

                {/* Advanced Filters Toggle */}
                <div className="flex items-end">
                  <UnifiedButton
                    variant="secondary"
                    size="small"
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <Filter className="w-4 h-4" />
                    Advanced
                    <ChevronDown className={`w-4 h-4 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
                  </UnifiedButton>
                </div>
              </div>

              {/* Job Type Filter */}
              <div>
                <label className={`${typography?.label} ${colors?.text?.primary} mb-3 block`}>Job Type</label>
                <div className="flex flex-wrap gap-2">
                  {jobTypeOptions?.map((option) => (
                    <label key={option?.value} className={`flex items-center space-x-2 ${typography?.body?.small} ${colors?.text?.primary}`}>
                      <input
                        type="checkbox"
                        checked={filters?.jobTypes?.includes(option?.value) || false}
                        onChange={(e) => {
                          const currentTypes = filters?.jobTypes || [];
                          let newTypes;
                          if (e?.target?.checked) {
                            newTypes = [...currentTypes, option?.value];
                          } else {
                            newTypes = currentTypes?.filter(type => type !== option?.value);
                          }
                          updateFilter('jobTypes', newTypes);
                        }}
                        className="w-4 h-4 rounded text-accent focus:ring-accent"
                      />
                      <span>{option?.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Enhanced Advanced Filters Section */}
              {showAdvancedFilters && (
                <div className={`space-y-6 pt-6 border-t ${colors?.border?.default}`}>
                  <h4 className={`${typography?.h4} ${colors?.text?.primary} flex items-center gap-2`}>
                    <Star className="w-4 h-4 text-accent" />
                    Advanced Filters
                  </h4>

                  {/* Premium AI-Powered Filters (available for authenticated job seekers) */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Crown className="w-4 h-4 text-amber-500" />
                      <span className="text-scale-14 font-medium text-text">AI-Powered Filters</span>
                      <Chip size="sm" className="bg-amber-100 text-amber-800 border-amber-200">
                        Premium
                      </Chip>
                    </div>

                    {premiumFilters?.map((filter) => {
                      const FilterIcon = filter?.icon;
                      
                      return (
                        <div key={filter?.key}>
                          <label className="flex items-center space-x-2 text-scale-14 font-medium text-text mb-3">
                            <FilterIcon className="w-4 h-4" />
                            <span>{filter?.label}</span>
                            <Chip size="sm" className="bg-green-100 text-green-800 border-green-200">
                              AI
                            </Chip>
                          </label>
                          {filter?.type === 'range' ? (
                            <div className="grid grid-cols-2 gap-3">
                              <Input 
                                placeholder={`Min ${filter?.min}%`}
                                value={filters?.[filter?.key]?.min || ''}
                                onChange={(e) => updateFilter(filter?.key, { 
                                  ...filters?.[filter?.key], 
                                  min: e?.target?.value 
                                })}
                              />
                              <Input 
                                placeholder={`Max ${filter?.max}%`}
                                value={filters?.[filter?.key]?.max || ''}
                                onChange={(e) => updateFilter(filter?.key, { 
                                  ...filters?.[filter?.key], 
                                  max: e?.target?.value 
                                })}
                              />
                            </div>
                          ) : (
                            <MultiSelect
                              options={filter?.options}
                              value={filters?.[filter?.key] || []}
                              onChange={(selected) => updateFilter(filter?.key, selected)}
                              placeholder={`Select ${filter?.label?.toLowerCase()}...`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Experience Level */}
                  <div>
                    <label className={`flex items-center space-x-2 ${typography?.label} ${colors?.text?.primary} mb-3`}>
                      <Award className="w-4 h-4" />
                      <span>Experience Level</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {experienceLevelOptions?.map((option) => (
                        <label key={option?.value} className={`flex items-center space-x-2 ${typography?.body?.small} ${colors?.text?.primary}`}>
                          <input
                            type="checkbox"
                            checked={filters?.experienceLevel?.includes(option?.value) || false}
                            onChange={(e) => {
                              const current = filters?.experienceLevel || [];
                              let updated;
                              if (e?.target?.checked) {
                                updated = [...current, option?.value];
                              } else {
                                updated = current?.filter(level => level !== option?.value);
                              }
                              updateFilter('experienceLevel', updated);
                            }}
                            className="w-4 h-4 rounded text-accent focus:ring-accent"
                          />
                          <span>{option?.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Industries */}
                  <div>
                    <label className={`flex items-center space-x-2 ${typography?.label} ${colors?.text?.primary} mb-3`}>
                      <Building2 className="w-4 h-4" />
                      <span>Industries</span>
                    </label>
                    <MultiSelect
                      options={industryOptions}
                      value={filters?.industries || []}
                      onChange={(selected) => updateFilter('industries', selected)}
                      placeholder="Select industries..."
                    />
                  </div>

                  {/* Company Size */}
                  <div>
                    <label className={`flex items-center space-x-2 ${typography?.label} ${colors?.text?.primary} mb-3`}>
                      <Users className="w-4 h-4" />
                      <span>Company Size</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {companySizeOptions?.map((option) => (
                        <label key={option?.value} className={`flex items-center space-x-2 ${typography?.body?.small} ${colors?.text?.primary}`}>
                          <input
                            type="checkbox"
                            checked={filters?.companySize?.includes(option?.value) || false}
                            onChange={(e) => {
                              const current = filters?.companySize || [];
                              let updated;
                              if (e?.target?.checked) {
                                updated = [...current, option?.value];
                              } else {
                                updated = current?.filter(size => size !== option?.value);
                              }
                              updateFilter('companySize', updated);
                            }}
                            className="w-4 h-4 rounded text-accent focus:ring-accent"
                          />
                          <span>{option?.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Skills Filter */}
                  <div>
                    <label className={`flex items-center space-x-2 ${typography?.label} ${colors?.text?.primary} mb-3`}>
                      <Star className="w-4 h-4" />
                      <span>Required Skills</span>
                    </label>
                    <MultiSelect
                      options={skillsOptions}
                      value={filters?.skills || []}
                      onChange={(selected) => updateFilter('skills', selected)}
                      placeholder="Select required skills..."
                    />
                  </div>

                  {/* Languages Filter */}
                  <div>
                    <label className={`flex items-center space-x-2 ${typography?.label} ${colors?.text?.primary} mb-3`}>
                      <Languages className="w-4 h-4" />
                      <span>Languages</span>
                    </label>
                    <MultiSelect
                      options={languageOptions}
                      value={filters?.languages || []}
                      onChange={(selected) => updateFilter('languages', selected)}
                      placeholder="Select required languages..."
                    />
                  </div>

                  {/* Benefits Filter */}
                  <div>
                    <label className={`flex items-center space-x-2 ${typography?.label} ${colors?.text?.primary} mb-3`}>
                      <Heart className="w-4 h-4" />
                      <span>Benefits & Perks</span>
                    </label>
                    <MultiSelect
                      options={benefitsOptions}
                      value={filters?.benefits || []}
                      onChange={(selected) => updateFilter('benefits', selected)}
                      placeholder="Select desired benefits..."
                    />
                  </div>

                  {/* Salary Range Filter */}
                  <div>
                    <label className={`flex items-center space-x-2 ${typography?.label} ${colors?.text?.primary} mb-3`}>
                      <DollarSign className="w-4 h-4" />
                      <span>Salary Range</span>
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-muted mb-1">Currency</label>
                        <select
                          value={filters?.salaryRange?.currency || 'USD'}
                          onChange={(e) => updateFilter('salaryRange', {
                            ...filters?.salaryRange,
                            currency: e?.target?.value
                          })}
                          className="w-full px-3 py-2 bg-bg border border-border rounded-medium text-text text-scale-14 focus:outline-none focus:ring-2 focus:ring-accent"
                        >
                          <option value="USD">USD ($)</option>
                          <option value="CAD">CAD (C$)</option>
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted mb-1">Minimum</label>
                        <Input
                          type="number"
                          value={filters?.salaryRange?.min || ''}
                          onChange={(e) => updateFilter('salaryRange', {
                            ...filters?.salaryRange,
                            min: e?.target?.value
                          })}
                          placeholder="50000"
                          className="text-scale-14"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted mb-1">Maximum</label>
                        <Input
                          type="number"
                          value={filters?.salaryRange?.max || ''}
                          onChange={(e) => updateFilter('salaryRange', {
                            ...filters?.salaryRange,
                            max: e?.target?.value
                          })}
                          placeholder="100000"
                          className="text-scale-14"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Active Filters Display */}
              {hasActiveFilters() && (
                <div className={`pt-4 border-t ${colors?.border?.default}`}>
                  <div className="flex items-center justify-between">
                    <span className={`${typography?.body?.small} font-medium ${colors?.text?.primary}`}>Active Filters:</span>
                    <UnifiedButton
                      variant="ghost"
                      size="small"
                      onClick={clearAllFilters}
                      className={colors?.status?.error}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear All
                    </UnifiedButton>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-3">
                    {filters?.location?.city && filters?.location?.city !== '' && (
                      <Chip 
                        size="sm" 
                        variant="accent"
                        onRemove={() => updateFilter('location', { city: '' })}
                      >
                        📍 {filters?.location?.city}
                      </Chip>
                    )}
                    {filters?.workMode && filters?.workMode !== 'ANY' && (
                      <Chip 
                        size="sm" 
                        variant="accent"
                        onRemove={() => updateFilter('workMode', 'ANY')}
                      >
                        💼 {filters?.workMode}
                      </Chip>
                    )}
                    {filters?.jobTypes && filters?.jobTypes?.map((type) => (
                      <Chip 
                        key={type}
                        size="sm" 
                        variant="accent"
                        onRemove={() => updateFilter('jobTypes', filters?.jobTypes?.filter(t => t !== type))}
                      >
                        {type}
                      </Chip>
                    ))}
                    {filters?.industries && filters?.industries?.map((industry) => (
                      <Chip 
                        key={industry}
                        size="sm" 
                        variant="accent"
                        onRemove={() => updateFilter('industries', filters?.industries?.filter(i => i !== industry))}
                      >
                        🏢 {industryOptions?.find(opt => opt?.value === industry)?.label || industry}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}

              {/* Sort By */}
              <div className="flex justify-between items-center">
                <div className={`${typography?.body?.small} ${colors?.text?.secondary}`}>
                  {filteredJobs?.length} jobs found
                </div>
                <div className="flex items-center gap-3">
                  <label className={`${typography?.body?.small} font-medium ${colors?.text?.primary}`}>Sort by:</label>
                  <Select
                    id="sortBy"
                    description=""
                    error=""
                    value={sortBy}
                    onChange={setSortBy}
                    options={[
                      { value: 'relevance', label: 'Relevance' },
                      { value: 'date', label: 'Date posted' },
                      { value: 'salary', label: 'Salary' },
                      { value: 'company', label: 'Company' }
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </UnifiedCard>
      {/* Enhanced Map View Section */}
      {showMapView && (
        <UnifiedCard className="shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className={`${typography?.h3} ${colors?.text?.primary} flex items-center gap-2`}>
              <Map className="w-4 h-4 text-accent" />
              AI-Powered Map View
            </h3>
            <UnifiedButton
              variant="ghost"
              size="small"
              onClick={() => setShowMapView(false)}
              className={colors?.text?.secondary}
            >
              <X className="w-4 h-4" />
            </UnifiedButton>
          </div>
          
          <div className={`w-full h-64 ${colors?.background?.secondary} rounded-medium flex items-center justify-center`}>
            <div className={`text-center ${colors?.text?.secondary}`}>
              <Map className="w-12 h-12 mx-auto mb-2" />
              <p className={typography?.body?.small}>Interactive AI-powered map coming soon...</p>
              <p className={`${typography?.caption} mt-1`}>Smart job clustering, commute analysis, and location-based recommendations</p>
            </div>
          </div>
        </UnifiedCard>
      )}
      {/* Save Search Modal */}
      {showSaveSearchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <UnifiedCard className="w-full max-w-md mx-4 shadow-xl">
            <h3 className={`${typography?.h3} ${colors?.text?.primary} mb-4`}>Save Search</h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block ${typography?.label} ${colors?.text?.primary} mb-2`}>
                  Search Name
                </label>
                <UnifiedInput
                  value={savedSearchName}
                  onChange={(e) => setSavedSearchName(e?.target?.value)}
                  placeholder="e.g., Senior React Developer in San Francisco"
                  className="w-full"
                />
              </div>
              
              <div className={`${typography?.body?.small} ${colors?.text?.secondary}`}>
                <p className="mb-2">This search will be saved with {getActiveFilterCount()} filters:</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {filters?.location?.city && <p>• Location: {filters?.location?.city}</p>}
                  {filters?.workMode && filters?.workMode !== 'ANY' && <p>• Work Mode: {filters?.workMode}</p>}
                  {filters?.jobTypes && filters?.jobTypes?.length > 0 && <p>• Job Types: {filters?.jobTypes?.join(', ')}</p>}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <UnifiedButton
                variant="secondary"
                onClick={() => setShowSaveSearchModal(false)}
              >
                Cancel
              </UnifiedButton>
              <UnifiedButton
                variant="primary"
                onClick={handleSaveSearch}
                disabled={!savedSearchName?.trim()}
              >
                <Bookmark className="w-4 h-4 mr-2" />
                Save Search
              </UnifiedButton>
            </div>
          </UnifiedCard>
        </div>
      )}
      {/* Job Results */}
      <div className="space-y-4">
        {loading ? (
          <div className="grid gap-4">
            {[...Array(5)]?.map((_, i) => (
              <UnifiedCard key={i} className="animate-pulse">
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                </div>
              </UnifiedCard>
            ))}
          </div>
        ) : filteredJobs?.length > 0 ? (
          filteredJobs?.map((job) => (
            <UnifiedCard key={job?.id} hover className="cursor-pointer">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 
                      className={`${typography?.h3} ${colors?.text?.primary} hover:text-accent transition-colors`}
                      onClick={() => navigate(`/jobs/${job?.id}`)}
                    >
                      {job?.title}
                    </h3>
                    {job?.isFeatured && (
                      <Chip size="sm" variant="success">
                        <Star className="h-3 w-3 mr-1" />
                        Featured
                      </Chip>
                    )}
                    
                    {/* AI Match Score Badge */}
                    {job?.matchScore > 0 && (
                      <Chip size="sm" className="bg-purple-100 text-purple-800 border-purple-200">
                        <Sparkles className="h-3 w-3 mr-1" />
                        {Math.round(job?.matchScore)}% Match
                      </Chip>
                    )}
                  </div>

                  <div className={`flex items-center gap-4 ${typography?.body?.small} ${colors?.text?.secondary} mb-3`}>
                    <span className={`font-medium ${colors?.text?.primary}`}>{job?.companyName}</span>
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {formatLocation(job?.locations)}
                    </div>
                    <div className="flex items-center gap-1">
                      <span>{getWorkModeIcon(job?.workMode)}</span>
                      {job?.workMode}
                    </div>
                    {formatSalary(job?.salary) && (
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        {formatSalary(job?.salary)}
                      </div>
                    )}
                  </div>

                  {job?.tags && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {job?.tags?.slice(0, 4)?.map((tag, index) => (
                        <Chip key={index} size="sm" variant="secondary">
                          {tag}
                        </Chip>
                      ))}
                    </div>
                  )}

                  {/* AI Match Reasons */}
                  {job?.matchReasons?.length > 0 && (
                    <div className={`mt-3 p-2 ${colors?.background?.secondary} rounded-medium`}>
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5" />
                        <div>
                          <p className={`${typography?.caption} font-medium ${colors?.text?.primary} mb-1`}>
                            Why this matches:
                          </p>
                          <ul className={`${typography?.caption} ${colors?.text?.secondary} space-y-1`}>
                            {job?.matchReasons?.map((reason, idx) => (
                              <li key={idx}>• {reason}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={`flex items-center gap-4 ${typography?.caption} ${colors?.text?.secondary}`}>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {job?.postedAt ? new Date(job?.postedAt)?.toLocaleDateString() : 'Recently posted'}
                    </div>
                    <div className="flex items-center gap-1">
                      <Briefcase className="h-3 w-3" />
                      {job?.employmentType}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 ml-4">
                  <UnifiedButton
                    variant="ghost"
                    size="small"
                    onClick={(e) => {
                      e?.stopPropagation();
                      toggleSaveJob(job?.id);
                    }}
                    disabled={savedJobsLoading}
                    className="p-2"
                  >
                    {savedJobs?.has(job?.id) ? (
                      <BookmarkIcon className="h-5 w-5 text-accent fill-current" />
                    ) : (
                      <BookmarkOutline className="h-5 w-5" />
                    )}
                  </UnifiedButton>
                  
                  <UnifiedButton
                    variant="primary"
                    size="small"
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleApplyClick(job);
                    }}
                  >
                    Apply Now
                  </UnifiedButton>
                </div>
              </div>
            </UnifiedCard>
          ))
        ) : (
          <UnifiedCard className="text-center">
            <div className="max-w-md mx-auto">
              <Search className={`h-12 w-12 ${colors?.text?.muted} mx-auto mb-4`} />
              <h3 className={`${typography?.h3} ${colors?.text?.primary} mb-2`}>No jobs found</h3>
              <p className={`${typography?.body?.default} ${colors?.text?.secondary} mb-4`}>
                Try adjusting your search criteria or filters to find more opportunities.
              </p>
              <UnifiedButton onClick={clearAllFilters}>Clear filters</UnifiedButton>
            </div>
          </UnifiedCard>
        )}
      </div>
      {/* Application Modal */}
      {showApplicationModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <UnifiedCard className="w-full max-w-2xl mx-auto shadow-xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className={`${typography?.h2} ${colors?.text?.primary} mb-2`}>
                  Apply for {selectedJob?.title}
                </h2>
                <p className={`${typography?.body?.small} ${colors?.text?.secondary}`}>
                  at {selectedJob?.companyName}
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                disabled={applicationStatus?.submitting}
                className={`${colors?.text?.secondary} hover:${colors?.text?.primary} transition-colors`}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Duplicate Warning */}
            {duplicateCheck?.checking ? (
              <div className={`mb-6 p-4 ${colors?.background?.secondary} rounded-medium`}>
                <p className={`${typography?.body?.small} ${colors?.text?.secondary}`}>
                  Checking application status...
                </p>
              </div>
            ) : duplicateCheck?.isDuplicate ? (
              <div className={`mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-medium`}>
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                  <p className={`${typography?.body?.small} text-yellow-800 dark:text-yellow-200`}>
                    You have already applied to this position
                  </p>
                </div>
              </div>
            ) : null}

            {/* Success Message */}
            {applicationStatus?.success && (
              <div className={`mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-medium`}>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <p className={`${typography?.body?.small} text-green-800 dark:text-green-200`}>
                    Application submitted successfully! Closing modal...
                  </p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {applicationStatus?.error && (
              <div className={`mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-medium`}>
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className={`${typography?.body?.small} font-medium text-red-800 dark:text-red-200 mb-1`}>
                      Application Error
                    </p>
                    <p className={`${typography?.caption} text-red-700 dark:text-red-300`}>
                      {applicationStatus?.error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Application Form */}
            <div className="space-y-6">
              {/* Resume Upload */}
              <div>
                <label className={`block ${typography?.label} ${colors?.text?.primary} mb-3`}>
                  Resume <span className="text-red-500">*</span>
                </label>
                <div className={`border-2 border-dashed ${colors?.border?.default} rounded-medium p-6 text-center`}>
                  <input
                    type="file"
                    id="resume-upload"
                    accept=".pdf,.doc,.docx"
                    onChange={handleResumeChange}
                    disabled={applicationStatus?.submitting || duplicateCheck?.isDuplicate}
                    className="hidden"
                  />
                  <label
                    htmlFor="resume-upload"
                    className={`cursor-pointer ${applicationStatus?.submitting || duplicateCheck?.isDuplicate ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {applicationForm?.resumeFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <FileText className="h-8 w-8 text-accent" />
                        <div className="text-left">
                          <p className={`${typography?.body?.small} ${colors?.text?.primary} font-medium`}>
                            {applicationForm?.resumeFile?.name}
                          </p>
                          <p className={`${typography?.caption} ${colors?.text?.secondary}`}>
                            {(applicationForm?.resumeFile?.size / 1024 / 1024)?.toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e?.preventDefault();
                            setApplicationForm({ ...applicationForm, resumeFile: null });
                          }}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="h-12 w-12 mx-auto mb-3 text-accent" />
                        <p className={`${typography?.body?.small} ${colors?.text?.primary} mb-1`}>
                          Click to upload your resume
                        </p>
                        <p className={`${typography?.caption} ${colors?.text?.secondary}`}>
                          PDF or Word document (max 5MB)
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Cover Letter */}
              <div>
                <label className={`block ${typography?.label} ${colors?.text?.primary} mb-3`}>
                  Cover Letter (Optional)
                </label>
                <textarea
                  value={applicationForm?.coverLetter}
                  onChange={handleCoverLetterChange}
                  disabled={applicationStatus?.submitting || duplicateCheck?.isDuplicate}
                  placeholder="Tell the employer why you're a great fit for this role..."
                  rows={6}
                  className={`w-full px-4 py-3 ${colors?.background?.input} border ${colors?.border?.default} rounded-medium ${colors?.text?.primary} ${typography?.body?.small} focus:outline-none focus:ring-2 focus:ring-accent resize-none`}
                />
                <p className={`${typography?.caption} ${colors?.text?.secondary} mt-2`}>
                  {applicationForm?.coverLetter?.length} / 5000 characters
                </p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-8">
              <UnifiedButton
                variant="secondary"
                onClick={handleCloseModal}
                disabled={applicationStatus?.submitting}
              >
                Cancel
              </UnifiedButton>
              <UnifiedButton
                variant="primary"
                onClick={handleSubmitApplication}
                disabled={
                  applicationStatus?.submitting ||
                  !applicationForm?.resumeFile ||
                  duplicateCheck?.isDuplicate ||
                  applicationStatus?.success
                }
              >
                {applicationStatus?.submitting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </UnifiedButton>
            </div>
          </UnifiedCard>
        </div>
      )}
    </div>
  );
};

export default Jobs;